import { cn } from "@/lib/cn";

import { CalendarCell, EmptyCell } from "./CalendarCell";
import { formatDateKey, getCellStyles, getDaysInMonth } from "./calendarUtils";

interface DaysGridProps {
  year: number;
  month: number;
  dayStatsMap: Map<string, number>;
  selectedDate?: string | null;
  onSelectDay?: (date: string) => void;
  onHoverDay?: (date: string | null) => void;
  className?: string;
}

export const DaysGrid = ({
  year,
  month,
  dayStatsMap,
  selectedDate,
  onSelectDay,
  onHoverDay,
  className,
}: DaysGridProps) => {
  const firstDay = new Date(year, month, 1);
  // Понедельник = 0, Воскресенье = 6
  const firstWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = getDaysInMonth(year, month);

  // Создаём массив из null + дни месяца
  const cells: (number | null)[] = [];

  // Пустые ячейки в начале
  for (let i = 0; i < firstWeekday; i++) {
    cells.push(null);
  }

  // Дни месяца
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(day);
  }

  // Разбиваем на недели по 7 дней
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  return (
    // p-[2px] provides space for CalendarCell hover border (outside stroke)
    <div className={cn("flex w-48 shrink-0 flex-col gap-1 p-0.5", className)}>
      {weeks.map((week, weekIdx) => (
        <div
          key={weekIdx}
          className="flex items-center gap-1"
        >
          {week.map((day, dayIdx) => {
            if (day === null) {
              return <EmptyCell key={`empty-${weekIdx}-${dayIdx}`} />;
            }

            const date = new Date(year, month, day);
            const dateKey = formatDateKey(date);
            const percent = dayStatsMap.get(dateKey) ?? null;
            const { bg, text } = getCellStyles(percent);
            const isSelected = selectedDate === dateKey;

            return (
              <CalendarCell
                key={dateKey}
                day={day}
                bgClass={bg}
                textClass={text}
                isSelected={isSelected}
                onClick={() => onSelectDay?.(dateKey)}
                onMouseEnter={() => onHoverDay?.(dateKey)}
                onMouseLeave={() => onHoverDay?.(null)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};
